/**
 * frontend/lib/walletLink.js
 * Link a wallet to the signed-in account by PROVING ownership (Prompt 1.5).
 *
 *   1. GET   /api/users/wallet-nonce  → { nonce, message }  (single use, 10 min)
 *   2. wallet signs `message` with personal_sign (free, no transaction)
 *   3. PATCH /api/users/link-wallet   { wallet_address, signature, nonce }
 *      The server recovers the signer and links the address only if it matches.
 *
 * Throws an Error with a user-readable message on failure. A user rejecting the
 * signature in their wallet surfaces as err.code === 4001, like any other
 * wallet request.
 */

import { authFetch } from './authFetch';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001';

function utf8ToHex(text) {
  const bytes = new TextEncoder().encode(text);
  let hex = '0x';
  bytes.forEach(b => { hex += b.toString(16).padStart(2, '0'); });
  return hex;
}

export async function linkWalletWithSignature(address) {
  if (!window.ethereum) throw new Error('No wallet detected.');
  if (!address) throw new Error('No wallet address.');

  const nonceRes = await authFetch(`${API_BASE}/api/users/wallet-nonce`);
  const nonceBody = await nonceRes.json().catch(() => ({}));
  if (!nonceRes.ok || !nonceBody.message || !nonceBody.nonce) {
    throw new Error(nonceBody.error || 'Could not start wallet linking.');
  }

  // personal_sign takes [data, address]; hex-encoded UTF-8 is the form every
  // wallet accepts. The server verifies the same UTF-8 bytes.
  const signature = await window.ethereum.request({
    method: 'personal_sign',
    params: [utf8ToHex(nonceBody.message), address],
  });

  const linkRes = await authFetch(`${API_BASE}/api/users/link-wallet`, {
    method:  'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body:    JSON.stringify({ wallet_address: address, signature, nonce: nonceBody.nonce }),
  });
  const linkBody = await linkRes.json().catch(() => ({}));
  if (!linkRes.ok) {
    throw new Error(linkBody.error || 'Failed to link wallet.');
  }
  return linkBody.wallet || address.toLowerCase();
}
